"use client";

import { useEffect, useState } from "react";
import type { DiagramType } from "../types/types";
import { DiagramSelector } from "./DiagramSelector";
import Select from "react-select";
import { useTranslation } from "react-i18next";

type PropsType = {
  forcedBrand?: string;
  initialBrand?: string;
  initialYear?: string;
  initialTypeName?: string;
  initialModelId?: string;
  initialDiagramId?: string;

  initialData: any; // todo luci
  models: any; // todo luci
  diagramsByModel: any; // todo luci
  ownerManualLink: string | undefined;
  onBrandSelected: (brand: string | undefined) => void;
  onYearSelected: (year: string) => void;
  onTypeSelected: (typeName: string) => void;
  onModelSelected: (modelName: string) => void;
  onDiagramSelected: (diagram: DiagramType) => void;
};

type DropdownOption = { label: string; value: string };

export const Dropdowns = ({
  forcedBrand,
  initialData,
  initialBrand,
  initialYear,
  initialTypeName,
  initialModelId,
  initialDiagramId,
  models,
  diagramsByModel,
  ownerManualLink,
  onBrandSelected,
  onYearSelected,
  onTypeSelected,
  onModelSelected,
  onDiagramSelected,
}: PropsType) => {
  const [selectedBrandOption, setBrandOption] = useState<DropdownOption | null>(null);
  const [selectedYearOption, setYearOption] = useState<DropdownOption | null>(null);
  const [selectedRidingTypeOption, setRidingTypeOption] = useState<DropdownOption | null>(null);
  const [selectedModelOption, setModelOption] = useState<DropdownOption | null>(null);
  const [selectedDiagram, setSelectedDiagram] = useState<DiagramType | null>();

  useEffect(() => {
    if (initialBrand) {
      setBrandOption({ label: initialBrand.toUpperCase(), value: initialBrand });
    } else {
      setBrandOption(null);
    }

    if (initialYear) {
      setYearOption({ label: initialYear, value: initialYear });
    } else {
      setYearOption(null);
    }

    let ridingTypeId;
    if (initialBrand && initialYear && initialTypeName) {
      ridingTypeId = initialData[initialBrand][initialYear]?.types?.find((t: any) => t.name === initialTypeName)?.id;
      setRidingTypeOption({ label: initialTypeName, value: ridingTypeId });
    } else {
      setRidingTypeOption(null);
    }

    if (models && ridingTypeId && initialTypeName && initialModelId) {
      const modelName = models[ridingTypeId]?.find((t: any) => t.id === initialModelId)?.name;
      setModelOption({ label: modelName, value: initialModelId });
    } else {
      setModelOption(null);
    }

    if (initialModelId && diagramsByModel && initialDiagramId) {
      const diagram = diagramsByModel[initialModelId]?.find((t: any) => t.id === initialDiagramId);
      setSelectedDiagram(diagram);
    } else {
      setSelectedDiagram(null);
    }
  }, [initialData, initialBrand, initialYear, initialTypeName, initialModelId, initialDiagramId]);

  useEffect(() => {
    if (!selectedBrandOption && forcedBrand && initialData) {
      setBrandOption({ label: forcedBrand, value: forcedBrand });
    }
  }, [initialData, forcedBrand]);

  // Dropdown options
  const brandOptions = Object.keys(initialData).map((t) => ({ label: t.toUpperCase(), value: t }));
  const yearsOptions = selectedBrandOption
    ? Object.keys(initialData[selectedBrandOption.value])
        .sort((a, b) => parseInt(b) - parseInt(a))
        .map((t) => ({ label: t, value: t }))
    : [];

  const ridingTypeOptions =
    selectedBrandOption && selectedYearOption
      ? initialData[selectedBrandOption.value][selectedYearOption.value]?.types?.map((t: any) => ({ label: t.name, value: t.id }))
      : [];

  const modelOptions = selectedRidingTypeOption ? models[selectedRidingTypeOption.value]?.map((t: any) => ({ label: t.name, value: t.id })) : [];
  const diagrams = selectedModelOption ? diagramsByModel[selectedModelOption.value] : undefined;

  return (
    <div className="w-full mt-5 pb-5">
      <Selectors
        forcedBrand={forcedBrand}
        brandOptions={brandOptions}
        selectedBrandOption={selectedBrandOption}
        yearsOptions={yearsOptions}
        selectedYearOption={selectedYearOption}
        ridingTypeOptions={ridingTypeOptions}
        selectedRidingTypeOption={selectedRidingTypeOption}
        modelOptions={modelOptions}
        selectedModelOption={selectedModelOption}
        onBrandSelected={onBrandSelected}
        onYearSelected={onYearSelected}
        onTypeSelected={onTypeSelected}
        onModelSelected={onModelSelected}
      />

      {diagrams && selectedBrandOption?.value && (
        <DiagramSelector
          brand={selectedBrandOption?.value}
          diagrams={diagrams || []}
          selectedDiagram={selectedDiagram || undefined}
          ownerManualLink={ownerManualLink}
          onSelected={(diagram) => {
            onDiagramSelected(diagram);
          }}
        />
      )}
    </div>
  );
};

const Selectors = ({
  forcedBrand,
  brandOptions,
  selectedBrandOption,
  yearsOptions,
  selectedYearOption,
  ridingTypeOptions,
  selectedRidingTypeOption,
  modelOptions,
  selectedModelOption,
  onBrandSelected,
  onYearSelected,
  onTypeSelected,
  onModelSelected,
}: {
  forcedBrand: string | undefined;
  brandOptions: DropdownOption[];
  selectedBrandOption: DropdownOption | null;
  yearsOptions: DropdownOption[];
  selectedYearOption: DropdownOption | null;
  ridingTypeOptions: DropdownOption[];
  selectedRidingTypeOption: DropdownOption | null;
  modelOptions: DropdownOption[];
  selectedModelOption: DropdownOption | null;
  onBrandSelected: (brand: string | undefined) => void;
  onYearSelected: (year: string) => void;
  onTypeSelected: (typeName: string) => void;
  onModelSelected: (modelId: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="w-full flex flex-wrap sm:justify-start justify-center">
      <div className="flex flex-grow md:flex-none">
        {!forcedBrand && (
          <Select
            styles={{}}
            className="mt-1 text-black w-1/2 me-1 min-w-32 lg:min-w-48"
            placeholder="Brand"
            options={brandOptions}
            isSearchable={false}
            onChange={(newValue) => {
              onBrandSelected(newValue?.value);
            }}
            value={selectedBrandOption}
          />
        )}
        <Select
          className={"mt-1 text-black me-1 lg:min-w-48 " + (forcedBrand ? "min-w-48 lg:min-w-48 w-full" : "min-w-32 w-1/2")}
          placeholder={t("year_dropdown_placeholder")}
          options={yearsOptions}
          isSearchable={false}
          onChange={(newValue) => {
            if (newValue) {
              onYearSelected(newValue?.label);
            }
          }}
          value={selectedYearOption}
        />
      </div>
      <div className="flex flex-grow md:flex-none">
        <Select
          className="mt-1 text-black me-1 w-1/2 min-w-32 lg:min-w-48"
          placeholder={t("type_dropdown_placeholder")}
          options={ridingTypeOptions}
          isSearchable={false}
          onChange={(newValue) => {
            if (newValue) {
              onTypeSelected(newValue?.label);
            }
          }}
          value={selectedRidingTypeOption}
        />
        <Select
          className="mt-1 text-black me-1 w-1/2 min-w-32 lg:min-w-48"
          placeholder="Model"
          options={modelOptions}
          isSearchable={false}
          onChange={(newValue) => {
            if (newValue) {
              onModelSelected(newValue?.value);
            }
          }}
          value={selectedModelOption}
        />
      </div>
    </div>
  );
};
